import { websiteServices, reviews } from '@classify-static-data';

const FIRST_INSERT_AT = 8;
const INSERT_INTERVAL = 16;

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function pickRandom(list) {
  return list[Math.floor(Math.random() * list.length)];
}

function splitServiceLines(content) {
  const text = (content || '').trim();
  if (!text) return [];

  const stripNumberPrefix = (line) => line.replace(/^\d+\.\s*/, '').trim();

  // 按 "1.xxx 2.yyy 3.zzz" 分行，渲染时只保留圆点，不展示序号
  if (/\d+\./.test(text)) {
    const lines = text
      .split(/\s*(?=\d+\.)/)
      .map(stripNumberPrefix)
      .filter(Boolean);
    if (lines.length) return lines;
  }

  let lines = text.split(/\n+/).map(stripNumberPrefix).filter(Boolean);
  if (lines.length > 1) return lines;

  return [text];
}

function renderStars(rating) {
  const stars = Math.max(0, Math.min(5, Number(rating) || 0));
  return '&#9733;'.repeat(stars) + '&#9734;'.repeat(5 - stars);
}

function createCardShell(cardType) {
  const article = document.createElement('article');
  article.className = `ec type-${cardType}`;
  article.dataset.classifyCard = 'injected';
  article.dataset.cardType = cardType;
  article.innerHTML = `
    <div class="ec-bg" aria-hidden="true"></div>
    <div class="ec-noise" aria-hidden="true"></div>
    <div class="ec-circle c1" aria-hidden="true"></div>
    <div class="ec-circle c2" aria-hidden="true"></div>
  `;
  return article;
}

function appendContent(article, html) {
  const wrap = document.createElement('div');
  wrap.className = 'ec-content-wrap';
  wrap.innerHTML = html;
  article.appendChild(wrap);
  return article;
}

function renderTrustCard(item) {
  const lines = splitServiceLines(item.content)
    .map(
      (line) =>
        `<div class="ec-service-item"><span class="ec-service-dot" aria-hidden="true"></span>${escapeHtml(line)}</div>`
    )
    .join('');

  const article = createCardShell('trust');
  appendContent(
    article,
    `
      <div class="ec-tag">${escapeHtml(item.typeTitle)}</div>
      <h4 class="ec-title">${escapeHtml(item.mainTitle)}</h4>
      <div class="ec-service-list">${lines}</div>
    `
  );
  return article;
}

function renderReviewCard(item) {
  const article = createCardShell('review');
  const locationLine = `Verified Buyer &mdash; ${escapeHtml(item.location || '')}`;

  appendContent(
    article,
    `
      <div class="ec-tag">${escapeHtml(item.typeTitle)}</div>
      <span class="ec-quote-mark" aria-hidden="true">&ldquo;</span>
      <div class="ec-review-text">${escapeHtml(item.content)}</div>
      <div class="ec-reviewer-stars" aria-label="${item.starRating} out of 5 stars">${renderStars(item.starRating)}</div>
      <div class="ec-reviewer-name">${locationLine}</div>
    `
  );

  if (item.href) {
    article.classList.add('ec--linked');
    article.setAttribute('role', 'link');
    article.setAttribute('tabindex', '0');
    article.addEventListener('click', () => {
      window.location.href = item.href;
    });
    article.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        window.location.href = item.href;
      }
    });
  }

  return article;
}

function pickFirstCardType() {
  const hasTrust = websiteServices.length > 0;
  const hasReview = reviews.length > 0;
  if (hasTrust && hasReview) {
    return Math.random() < 0.5;
  }
  return hasTrust;
}

function buildCardForSlot(slotIndex, startWithTrust) {
  const wantTrust = startWithTrust ? slotIndex % 2 === 0 : slotIndex % 2 === 1;

  if (wantTrust && websiteServices.length) {
    return renderTrustCard(pickRandom(websiteServices));
  }
  if (!wantTrust && reviews.length) {
    return renderReviewCard(pickRandom(reviews));
  }
  if (websiteServices.length) {
    return renderTrustCard(pickRandom(websiteServices));
  }
  if (reviews.length) {
    return renderReviewCard(pickRandom(reviews));
  }
  return null;
}

function wrapCard(article) {
  const li = document.createElement('li');
  li.className = 'column classify-editorial-column';
  li.dataset.classifyCard = 'injected';
  li.appendChild(article);
  return li;
}

function getProductGrid() {
  return document.getElementById('gf-products') || document.getElementById('product-grid');
}

function getProductItems(grid) {
  return Array.from(grid.children).filter((node) => {
    if (node.nodeType !== 1) return false;
    if (node.dataset.classifyCard === 'injected') return false;
    if (node.classList.contains('classify-editorial-column')) return false;
    if (node.classList.contains('my-featured-product')) return false;
    if (node.id === 'collection-ads-img') return false;
    return node.matches('li.column, li.product-item, li[data-product-id]') || node.querySelector('product-card, .product-card');
  });
}

function removeInjectedCards(grid) {
  grid.querySelectorAll('[data-classify-card="injected"]').forEach((node) => node.remove());
}

function injectEditorialCards() {
  const grid = getProductGrid();
  if (!grid) return;

  removeInjectedCards(grid);

  const products = getProductItems(grid);
  if (!products.length) return;

  let productCount = 0;
  let cardSlotIndex = 0;
  const startWithTrust = pickFirstCardType();

  products.forEach((productLi) => {
    productCount += 1;
    const isFirstSlot = productCount === FIRST_INSERT_AT;
    const isLaterSlot =
      productCount > FIRST_INSERT_AT &&
      (productCount - FIRST_INSERT_AT) % INSERT_INTERVAL === 0;
    if (!isFirstSlot && !isLaterSlot) return;

    const card = buildCardForSlot(cardSlotIndex, startWithTrust);
    cardSlotIndex += 1;
    if (!card) return;

    productLi.insertAdjacentElement('afterend', wrapCard(card));
  });
}

function scheduleInject() {
  window.clearTimeout(scheduleInject.timer);
  scheduleInject.timer = window.setTimeout(injectEditorialCards, 120);
}

function initClassifyEditorialCards() {
  scheduleInject();

  window.addEventListener('globoFilterRenderCompleted', scheduleInject);

  const observer = new MutationObserver((mutations) => {
    const grid = getProductGrid();
    if (!grid) return;

    const touchedGrid = mutations.some((mutation) => {
      return Array.from(mutation.addedNodes).some((node) => {
        if (node.nodeType !== 1) return false;
        if (node.dataset.classifyCard === 'injected') return false;
        if (node.classList && node.classList.contains('classify-editorial-column')) return false;
        return node === grid || grid.contains(node);
      });
    });

    if (touchedGrid) scheduleInject();
  });

  const grid = getProductGrid();
  if (grid) {
    observer.observe(grid, { childList: true });
  } else {
    const bootObserver = new MutationObserver(() => {
      const nextGrid = getProductGrid();
      if (!nextGrid) return;
      bootObserver.disconnect();
      observer.observe(nextGrid, { childList: true });
      scheduleInject();
    });
    bootObserver.observe(document.body, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initClassifyEditorialCards);
} else {
  initClassifyEditorialCards();
}
